import { useQuery } from "@tanstack/react-query"
import type { AdminDashboardRange, AdminDashboardSummary } from "@ternary/contracts"
import { type Column, EmptyState, PageHeader, Skeleton, StatTile, Table, Tabs } from "@ternary/ui"
import { Link, useSearchParams } from "react-router-dom"
import { AdminApiError, loadAdminDashboardSummary } from "../api.js"
import { ChannelBadge } from "../components/channel-badge.js"
import { errorDetail, LoadError } from "../components/load-error.js"
import { NavIcon } from "../components/nav-icons.js"
import { SetupCard } from "../components/setup-card.js"
import { SignupLinkAction } from "../components/signup-link-action.js"
import { type FunnelRow, funnelRows } from "../journey.js"
import { shouldRetryAdminQuery } from "../query-client.js"

// One locale for every figure and date on the page, as on the customer list.
const COUNT = new Intl.NumberFormat("en-US")
const PERCENT = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0, style: "percent" })
const DATES = new Intl.DateTimeFormat("en-US", { dateStyle: "medium" })

/** A share from 0 to 1. One under 1% reads "<1%", so a channel with signups never reads "0%". */
function share(value: number) {
  return value > 0 && value < 0.01 ? "<1%" : PERCENT.format(value)
}

/** The ranges, and the calendar days to today that each one's channel links filter the list on. */
const RANGES = [
  { days: 1, id: "today", label: "Today", period: "today" },
  { days: 7, id: "7d", label: "7 days", period: "last 7 days" },
  { days: 30, id: "30d", label: "30 days", period: "last 30 days" },
] as const satisfies readonly {
  id: AdminDashboardRange
  days: number
  label: string
  period: string
}[]

type Range = (typeof RANGES)[number]
type ChannelRow = AdminDashboardSummary["byChannel"][number]

const DEFAULT_RANGE = RANGES[1]

/** A day in the viewer's time zone, as the customer list's From and To take it: YYYY-MM-DD. */
function listDay(date: Date) {
  const pad = (value: number) => String(value).padStart(2, "0")
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * The range as whole days in the viewer's time zone, today being the last. The list filters on
 * days, so a channel's link covers the 7 (or 30) calendar days to today, where the API counts the
 * 7 × 24 hours to now: the list can show a few more or fewer customers than the row.
 */
function rangeDays(range: Range) {
  const to = new Date()
  const from = new Date(to)
  from.setDate(from.getDate() - (range.days - 1))
  return { from, to }
}

/** A share of a whole, from 0 to 1, as a bar. Decorative: its row says the number. */
function Bar({ value }: { value: number }) {
  return (
    <span aria-hidden="true" className="admin-bar">
      <span
        className="admin-bar-fill"
        style={{ width: `${Math.max(value * 100, value > 0 ? 1 : 0)}%` }}
      />
    </span>
  )
}

/** The loading state, shaped like the page: the dates, three stat tiles, two table frames. */
function DashboardSkeleton() {
  return (
    <div aria-busy="true" className="admin-stack admin-dashboard">
      <p className="tny-visually-hidden" role="status">
        Loading the dashboard…
      </p>
      <Skeleton width="12rem" />
      <div className="admin-stats">
        {["signups", "joins", "rate"].map((tile) => (
          <div className="tny-stat" key={tile}>
            <Skeleton width="5rem" />
            <Skeleton height="2.25rem" width="4rem" />
          </div>
        ))}
      </div>
      <div className="admin-dashboard-tables">
        {["channels", "funnel"].map((table) => (
          <div className="tny-table-frame admin-skeleton-frame" key={table}>
            <Skeleton lines={6} />
          </div>
        ))}
      </div>
    </div>
  )
}

/** One range's tiles, channel split and funnel. Only the open tab mounts, so only it loads. */
function RangePanel({ range }: { range: Range }) {
  const query = useQuery({
    queryFn: ({ signal }) => loadAdminDashboardSummary(range.id, { signal }),
    queryKey: ["admin-dashboard", range.id],
    // A 503 already waited out the API's 5 s limit: retrying at once would make the admin wait
    // twice. Retry is theirs.
    retry: (count, error) =>
      !(error instanceof AdminApiError && error.status === 503) &&
      shouldRetryAdminQuery(count, error),
  })

  if (query.isPending) {
    return <DashboardSkeleton />
  }

  if (query.isError) {
    // A 503 is a read past the API's 5 s limit (or a failed one): waiting a moment is the advice.
    const slow = query.error instanceof AdminApiError && query.error.status === 503
    return (
      <LoadError
        description={slow ? "Try again in a moment." : undefined}
        detail={errorDetail(query.error)}
        message={slow ? "The dashboard took too long to load" : "We couldn't load the dashboard"}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    )
  }

  const data = query.data
  const { from, to } = rangeDays(range)
  const filter = { from: listDay(from), to: listDay(to) }
  const shareOf = (row: ChannelRow) => (data.signups ? row.signups / data.signups : 0)
  // Until someone lands in the range, a step that counted nobody is only a row of "0 —".
  const allSteps = funnelRows(data.funnel)
  const landed = allSteps.some((row) => row.step === "landing" && row.count > 0)
  const funnel = landed ? allSteps : allSteps.filter((row) => row.count > 0)
  const largest = Math.max(0, ...funnel.map((row) => row.count))

  const channelColumns: Column<ChannelRow>[] = [
    {
      cell: (row) => (
        <span className="admin-bar-cell">
          <Link
            className="admin-channel-link"
            to={`/dashboard/customers?${new URLSearchParams({ channel: row.channel, ...filter })}`}
          >
            <ChannelBadge channel={row.channel} />
            <NavIcon name="forward" />
          </Link>
          <Bar value={shareOf(row)} />
        </span>
      ),
      header: "Channel",
      key: "channel",
    },
    { align: "end", cell: (row) => COUNT.format(row.signups), header: "Signups", key: "signups" },
    { align: "end", cell: (row) => COUNT.format(row.joins), header: "Joins", key: "joins" },
    {
      align: "end",
      cell: (row) => share(shareOf(row)),
      header: "Share",
      key: "share",
      priority: 2,
    },
  ]
  const funnelColumns: Column<FunnelRow>[] = [
    {
      // Each bar against the largest stage, never the stage before: the stages are not nested.
      cell: (row) => (
        <span className="admin-bar-cell">
          {row.label}
          <Bar value={largest ? row.count / largest : 0} />
        </span>
      ),
      header: "Step",
      key: "step",
    },
    { align: "end", cell: (row) => COUNT.format(row.count), header: "Count", key: "count" },
    {
      align: "end",
      cell: (row) => (
        <span className="admin-note">
          {row.dropOff === null ? "—" : PERCENT.format(row.dropOff)}
        </span>
      ),
      // A non-breaking hyphen, so the narrow phone column does not split the header.
      header: "Drop‑off",
      key: "dropOff",
    },
  ]

  return (
    <div className="admin-stack admin-dashboard">
      <p className="admin-note">{DATES.formatRange(from, to)}</p>
      <div className="admin-stats">
        {/* R61: the API counts people, not signups. */}
        <StatTile
          hint="People who confirmed their email"
          label="Signups"
          value={COUNT.format(data.signups)}
        />
        <StatTile
          hint="People who joined the Discord server"
          label="Joins"
          value={COUNT.format(data.joins)}
        />
        <StatTile
          hint={data.signups ? "Share of signups who joined Discord" : "No signups yet"}
          label="Join rate"
          value={data.signups ? PERCENT.format(data.joins / data.signups) : "—"}
        />
      </div>
      {data.signups === 0 && data.joins > 0 ? (
        // People who signed up before the range can still join in it.
        <p className="admin-note">
          {range.id === "today"
            ? "No new signups today."
            : `No new signups in the ${range.period}.`}{" "}
          Everyone who joined signed up earlier.
        </p>
      ) : null}
      {data.signups === 0 && data.joins === 0 ? (
        <EmptyState
          action={<SignupLinkAction />}
          description="Share your signup link. People count here once they confirm their email."
          title={range.id === "today" ? "No signups today" : `No signups in the ${range.period}`}
        />
      ) : null}
      <div className="admin-dashboard-tables">
        {/* Someone who confirmed earlier can still join in the range. */}
        {data.signups > 0 || data.joins > 0 ? (
          <Table
            caption={`By channel, ${range.period}`}
            columns={channelColumns}
            rowKey={(row) => row.channel}
            rows={data.byChannel}
          />
        ) : null}
        <div className="admin-stack">
          <Table
            caption={`Signup funnel, ${range.period}`}
            columns={funnelColumns}
            empty={
              <p className="admin-note">
                {range.id === "today" ? "No visits today." : `No visits in the ${range.period}.`}
              </p>
            }
            rowKey={(row) => row.step}
            rows={funnel}
          />
          <p className="admin-note">
            Landing counts visits; the other steps count signups. Each step counts what happened in
            this range, so a step can count more than the one before it, and the join rate can pass
            100% when people who signed up earlier join.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Where an admin lands: signups and joins for today, 7 days or 30 days, split by channel, and the
 * signup funnel (§3.5). The range is in the URL, so Back from a channel's customers returns to it.
 */
export function AdminDashboardPage() {
  const [params, setParams] = useSearchParams()
  const range = RANGES.find((each) => each.id === params.get("range")) ?? DEFAULT_RANGE

  return (
    <section className="admin-stack admin-page">
      <PageHeader description="Signups and joins from your signup pages." title="Dashboard" />
      <SetupCard />
      <Tabs
        label="Date range"
        onChange={(id) =>
          setParams(id === DEFAULT_RANGE.id ? {} : { range: id }, { replace: true })
        }
        tabs={RANGES.map((each) => ({
          content: each.id === range.id ? <RangePanel range={each} /> : null,
          id: each.id,
          label: each.label,
        }))}
        value={range.id}
      />
    </section>
  )
}
